import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { BlymLoading } from '../../components/loading';
import { MobileDrawerWithAutoClose } from '../../components/mobile-drawer';
import { MobileHeader } from '../../components/mobile-header';
import { type Organization } from '../../entity/api/organization';
import { OrganizationInviteStatus } from '../../entity/api/organization-user';
import { InternalServerError } from '../../error';
import { addTrackCrumb, trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import {
  getStores,
  useAuthUser,
  useBrandsList,
  useOrganizationBilling,
  useOrganizationUsersByUserId,
  useOrganizationsList,
} from '../../services/storage';
import { UIKey } from '../../services/storage/ui-key-value';
import { tGlobal } from '../../services/translation';
import { getSelectedBrand } from '../../shared/brand';
import { getSelectedOrganization } from '../../shared/organization';
import { Sidebar } from './-sidebar';

export const Route = createFileRoute('/app/_sidebar-layout')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/_sidebar-layout',
      level: 'info',
    });
  },

  // eslint-disable-next-line no-use-before-define
  component: SidebarLayout,
});

export function SidebarLayout() {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // TanStack Query hooks for data fetching
  const { data: authUser } = useAuthUser();
  const { data: brands } = useBrandsList();
  const { data: organizations } = useOrganizationsList();

  // TanStack Query for org users filtered by user ID
  const { data: userOrgsData } = useOrganizationUsersByUserId(authUser?._id);

  const currentOrganization = getSelectedOrganization(authUser);
  const currentBrand = getSelectedBrand(authUser, currentOrganization?._id);
  const isOrgLoading = getStores().uiKeyValue(function (state) {
    return state.getFromStore(UIKey.IS_ORG_LOADING) ?? false;
  });

  // TanStack Query for organization billing
  const { data: currentOrgBillingData } = useOrganizationBilling(currentOrganization?._id);

  // Transform data to match expected format
  // TODO: remove after 1:1 migration
  const brandsQuery = { data: brands ?? [] };
  const organizationListQuery = useMemo(() => ({ data: organizations ?? [] }), [organizations]);
  const currentOrgBilling = { data: currentOrgBillingData != null ? [currentOrgBillingData] : [] };

  const organizationList = useMemo(
    function () {
      const orgList: Organization[] = [];
      const userOrgs = userOrgsData ?? [];
      if (organizationListQuery == null || userOrgs.length === 0) {
        return orgList;
      }

      for (const org of organizationListQuery?.data ?? []) {
        if (org == null) {
          continue;
        }

        const userOrg = userOrgs.find(
          (uo) =>
            uo != null &&
            uo.organizationId === org._id &&
            uo.inviteStatus === OrganizationInviteStatus.Accepted
        );
        if (userOrg != null) {
          orgList.push(org);
        }
      }

      return orgList;
    },
    [organizationListQuery, userOrgsData]
  );

  useEffect(
    function () {
      // a current brand and a current organization is always required
      // it is possible specially on legacy organizations to not have a brand
      // for example. we want to make sure they are redirected on those cases
      let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(function () {
        if (currentOrganization != null && currentBrand != null) {
          // nothing to do on this case, all was handled
          return;
        }

        // handle the case where we don't have a brand
        if (currentBrand == null && pathname !== '/app/no-brand') {
          window.location.href = `/app/no-brand`;
          return;
        }
      }, 5000);

      return function () {
        if (timer == null) {
          return;
        }

        clearTimeout(timer);
        timer = undefined;
      };
    },
    [currentBrand, currentOrganization, pathname]
  );

  useEffect(
    function () {
      // it is possible to land on this loading and get stuck on it, as such,
      // we want to make sure that if there is something wrong with the data
      // and we keep getting on the loading state, we force a timeout and
      // logout clearing all the data
      let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(
        function () {
          if (currentOrganization != null || currentBrand != null) {
            // nothing to do on this case, all was handled
            return;
          }

          // capture the timeout
          captureException(
            new InternalServerError('timeout when looking for current user data'),
            {
              file: 'app/_sidebar-layout.tsx',
              method: 'useEffect',
              infoMessage: 'timeout when looking for current user data',
              currentBrand,
              currentOrganization,
              pathname,
              authUserID: authUser?._id,
            },
            trackError
          );

          // sign out the user and force a refresh on sign in
          getStores()
            .users.getState()
            .signOut()
            .then(function () {
              window.location.href = `/auth`;
            })
            .catch(function (err) {
              captureException(
                err,
                {
                  file: 'app/_sidebar-layout.tsx',
                  method: 'useEffect',
                  infoMessage: 'unable to logout after timeout',
                  currentBrand,
                  currentOrganization,
                  pathname,
                  authUserID: authUser?._id,
                },
                trackError
              );
            });
        },
        1000 * 60 * 5
      ); // 5 min

      return function () {
        if (timer == null) {
          return;
        }

        clearTimeout(timer);
        timer = undefined;
      };
    },
    [authUser, currentBrand, currentOrganization, pathname]
  );

  // Update brand-context URL when brand changes (e.g., when switching organizations)
  useEffect(
    function () {
      // Only update URL if we're on the brand-context page
      if (!pathname.includes('/app/brand-context')) {
        return;
      }

      // Don't update if brand is not available yet or organization is loading
      if (currentBrand == null || isOrgLoading) {
        return;
      }

      // Get current brandId from URL search params
      const urlParams = new URLSearchParams(window.location.search);
      const urlBrandId = urlParams.get('brandId');

      // If URL brandId doesn't match current brand, update the URL
      if (urlBrandId !== currentBrand._id) {
        navigate({
          to: '/app/brand-context',
          search: { brandId: currentBrand._id },
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.tsx',
              method: 'useEffect',
              infoMessage: 'unable to navigate to brand-context with updated brandId',
              currentBrandId: currentBrand._id,
              urlBrandId,
            },
            trackError
          );
        });
      }
    },
    [currentBrand, pathname, isOrgLoading, navigate]
  );

  // Update seo-strategy URL when brand changes or when brandId is invalid (e.g. "undefined")
  useEffect(
    function () {
      if (!pathname.startsWith('/app/seo-strategy/')) {
        return;
      }

      if (currentBrand == null || isOrgLoading) {
        return;
      }

      const urlBrandId = pathname.match(/\/app\/seo-strategy\/([^/]+)/)?.[1];

      if (urlBrandId !== currentBrand._id) {
        navigate({
          to: '/app/seo-strategy/$brandId/overview',
          params: { brandId: currentBrand._id },
          replace: true,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.tsx',
              method: 'useEffect',
              infoMessage: 'unable to navigate to seo-strategy with updated brandId',
              currentBrandId: currentBrand._id,
              urlBrandId,
            },
            trackError
          );
        });
      }
    },
    [currentBrand, pathname, isOrgLoading, navigate]
  );

  if (
    (currentOrganization == null && currentBrand == null) ||
    (currentBrand == null && pathname !== '/app/no-brand')
  ) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-white">
        <BlymLoading />
        <p className="font-display-2">{tGlobal('loadingWorkspace')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden overflow-x-hidden">
      {/* Mobile Header - shown only on mobile */}
      <div className="mobile:block desktop:hidden">
        <MobileHeader
          onMenuClick={function () {
            setIsMobileMenuOpen(true);
          }}
        />
      </div>

      {/* Mobile Drawer - shown only on mobile */}
      <div className="mobile:block desktop:hidden">
        <MobileDrawerWithAutoClose
          open={isMobileMenuOpen}
          onOpenChange={setIsMobileMenuOpen}
          pathname={pathname}
        >
          <Sidebar
            user={authUser}
            organizations={organizationList}
            currentOrganization={currentOrganization ?? undefined}
            orgUsers={(userOrgsData ?? []).filter((s) => s != null)}
            brands={brandsQuery.data.filter((s) => s != null)}
            currentBrand={currentBrand ?? undefined}
            currentPath={pathname}
            currentOrgBilling={currentOrgBilling.data?.[0] ?? undefined}
            variant="mobile"
          />
        </MobileDrawerWithAutoClose>
      </div>

      <div className="flex h-full w-full min-w-0">
        {/* Desktop Sidebar - shown only on desktop */}
        <div className="mobile:hidden desktop:flex">
          <Sidebar
            user={authUser}
            organizations={organizationList}
            currentOrganization={currentOrganization ?? undefined}
            orgUsers={(userOrgsData ?? []).filter((s) => s != null)}
            brands={brandsQuery.data.filter((s) => s != null)}
            currentBrand={currentBrand ?? undefined}
            currentPath={pathname}
            currentOrgBilling={currentOrgBilling.data?.[0] ?? undefined}
            variant="desktop"
          />
        </div>

        {/* Main Content Area */}
        <div className="flex h-full w-full min-w-0 flex-col overflow-hidden mobile:pt-16">
          {isOrgLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <BlymLoading />
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}
